import { useLocation } from "@gatsbyjs/reach-router"
import React from "react"
import { Helmet } from "react-helmet"

import ogImageAsset from "../../assets/images/tf-typosymbol.jpg"
import { ENV } from "../../constants/env"

// Absolute origin + path. Crawlers read the server-rendered HTML, where `window`
// does not exist, so the URL must never be derived from it — doing so collapsed
// every canonical onto the site root and told Google each page was a duplicate
// of the homepage.
const absoluteUrl = path => {
  if (!path) return ENV.CANONICAL_ORIGIN
  if (/^https?:\/\//i.test(path)) return path
  return `${ENV.CANONICAL_ORIGIN}${path.startsWith("/") ? path : `/${path}`}`
}

// Gatsby is configured with the default `trailingSlash: "always"`, so the
// canonical has to match the URL actually served or the two disagree.
const withTrailingSlash = pathname => {
  if (!pathname) return "/"
  if (pathname.includes(".")) return pathname // /sitemap.xml and friends
  return pathname.endsWith("/") ? pathname : `${pathname}/`
}

const Head = ({ title, meta = [], description, image, url, keywords, pathname, children }) => {
  const location = useLocation()

  const defaultDescription =
    "Buy tickets for your favorite sports events on TFSports. Secure, easy checkout for all major sporting events."
  const defaultKeywords =
    "sports tickets, buy tickets, sports events, TFSports, event tickets, sports ticketing, online tickets"
  const pageTitle = title || "tfsports - Gerenciar tickets"
  const pageDescription = description || defaultDescription
  const pageKeywords = keywords || defaultKeywords
  const pageImage = absoluteUrl(image || ogImageAsset)

  const pageUrl = url || absoluteUrl(withTrailingSlash(pathname || location?.pathname))

  const metaTags = [
    {
      name: "description",
      content: pageDescription
    },
    {
      name: "keywords",
      content: pageKeywords
    },
    // Open Graph tags
    {
      property: "og:site_name",
      content: "TFSports"
    },
    {
      property: "og:title",
      content: pageTitle
    },
    {
      property: "og:description",
      content: pageDescription
    },
    {
      property: "og:type",
      content: "website"
    },
    {
      property: "og:url",
      content: pageUrl
    },
    {
      property: "og:image",
      content: pageImage
    },
    {
      property: "og:image:alt",
      content: pageTitle
    },
    // Twitter Card tags
    {
      name: "twitter:card",
      content: "summary_large_image"
    },
    {
      name: "twitter:title",
      content: pageTitle
    },
    {
      name: "twitter:description",
      content: pageDescription
    },
    {
      name: "twitter:image",
      content: pageImage
    },
    {
      name: "twitter:image:alt",
      content: pageTitle
    },
    ...meta
  ]

  return (
    <>
      <Helmet
        defaultTitle="tfsports - Gerenciar tickets"
        title={title}
        meta={metaTags}
        htmlAttributes={{ lang: "en-US" }}
      >
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link rel="canonical" href={pageUrl} />
        {children}
      </Helmet>
    </>
  )
}

export default Head
